import { types as sdkTypes } from './sdkLoader';

const { LatLng, LatLngBounds } = sdkTypes;

export const LISTING_PAGE_PENDING_APPROVAL_VARIANT = 'pending-approval';
export const LISTING_PAGE_DRAFT_VARIANT = 'draft';

export const LISTING_PAGE_PARAM_TYPE_NEW = 'new';
export const LISTING_PAGE_PARAM_TYPE_DRAFT = 'draft';
export const LISTING_PAGE_PARAM_TYPE_EDIT = 'edit';
export const LISTING_PAGE_PARAM_TYPES = [
  LISTING_PAGE_PARAM_TYPE_NEW,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
];

export const PROFILE_PAGE_PENDING_APPROVAL_VARIANT = 'pending-approval';

// No access page - path params:
export const NO_ACCESS_PAGE_POST_LISTINGS = 'posting-rights';
export const NO_ACCESS_PAGE_INITIATE_TRANSACTIONS = 'transaction-rights';
// If user account is on pending-approval state, then user can't initiate transactions or create listings
export const NO_ACCESS_PAGE_USER_PENDING_APPROVAL = 'user-approval';
// If user account has no viewing rights on a private marketplace, they can only see their own listings
export const NO_ACCESS_PAGE_VIEW_LISTINGS = 'viewing-rights';

// ================ Overlay state that lives in the URL ================ //

// The Topbar keeps its overlays open by writing a query param, so that the back button closes them
// and a reload keeps them open. These are UI state that happens to travel in the URL — they are
// never page or search input, and the rest of the app has to be able to tell the difference.
// OrderPanel does the same for the booking panel it shows as a full-screen modal on mobile.
export const AUTH_MODAL_PARAM = 'authmodal';
export const MOBILE_MENU_PARAM = 'mobilemenu';
export const MOBILE_SEARCH_PARAM = 'mobilesearch';
export const ORDER_PANEL_PARAM = 'orderOpen';
export const UI_ONLY_PARAMS = [
  AUTH_MODAL_PARAM,
  MOBILE_MENU_PARAM,
  MOBILE_SEARCH_PARAM,
  ORDER_PANEL_PARAM,
];

// Deliberately built on URLSearchParams rather than the parse/stringify pair below: those two
// round-trip values through SDK types and re-sort the keys, which is more than an overlay toggle
// should ever do to a URL somebody else composed.
const searchParamsOf = search => new URLSearchParams(typeof search === 'string' ? search : '');
const searchToString = params => {
  const str = params.toString();
  return str ? `?${str}` : '';
};

/**
 * Drop the overlay params from a parsed query object.
 *
 * Anything that forwards URL params onwards — to the Marketplace API above all — has to run them
 * through this first. Verified 2026-08-07: without it, opening the login dialog on the search page
 * put `authmodal=open` into the outgoing `listings/query`.
 *
 * @param {Object} params - parsed query params
 * @returns {Object} the same params without the overlay ones
 */
export const omitUiOnlyParams = (params = {}) =>
  Object.keys(params).reduce((picked, key) => {
    if (!UI_ONLY_PARAMS.includes(key)) {
      picked[key] = params[key];
    }
    return picked;
  }, {});

/**
 * Whether the only thing that moved between two locations is an overlay param.
 *
 * Opening or closing an overlay is not a navigation, and treating it as one has cost us three
 * separate things: a refetch of the page's data, a scroll back to the top, and a spurious GA4
 * page_view (canonicalRoutePath includes the query string).
 *
 * Note the last condition: an overlay param must actually have *changed*. Re-pushing the very same
 * URL — clicking "search" again with unchanged filters, say — has always refetched, and this is
 * not the place to change that.
 *
 * @param {Object} prev - previous location
 * @param {Object} next - current location
 * @returns {boolean}
 */
export const isUiOnlyLocationChange = (prev, next) => {
  if (!prev || !next) {
    return false;
  }
  if (prev.pathname !== next.pathname || (prev.hash || '') !== (next.hash || '')) {
    return false;
  }

  const prevParams = searchParamsOf(prev.search);
  const nextParams = searchParamsOf(next.search);
  const prevOverlay = UI_ONLY_PARAMS.map(p => `${p}=${prevParams.get(p)}`).join('&');
  const nextOverlay = UI_ONLY_PARAMS.map(p => `${p}=${nextParams.get(p)}`).join('&');
  if (prevOverlay === nextOverlay) {
    return false;
  }

  UI_ONLY_PARAMS.forEach(p => {
    prevParams.delete(p);
    nextParams.delete(p);
  });
  // Sorted, because the two ways of opening an overlay do not agree on key order: the login dialog
  // appends its param and leaves the rest of the query untouched, while the mobile menu's
  // redirectToURLWithModalState round-trips everything through stringify(), which sorts. Comparing
  // raw order would have quietly let the mobile menu through as a real navigation on any page whose
  // query was not already sorted.
  prevParams.sort();
  nextParams.sort();
  return prevParams.toString() === nextParams.toString();
};

/**
 * Split a path string back into the location object React Router's `<Redirect to>` takes.
 *
 * The auth flow moves the page a visitor was trying to reach around as a plain string (`from`),
 * because that is all the OAuth round-trip's cookie can carry. Sending them back there with their
 * history state intact needs the object form, so the string has to be taken apart again.
 *
 * @param {string} path - e.g. `/soft-booking-checkout?a=1#b`
 * @returns {{pathname: string, search: string, hash: string}}
 */
export const locationFromPath = path => {
  const str = typeof path === 'string' ? path : '';
  const hashIndex = str.indexOf('#');
  const hash = hashIndex >= 0 ? str.slice(hashIndex) : '';
  const withoutHash = hashIndex >= 0 ? str.slice(0, hashIndex) : str;
  const searchIndex = withoutHash.indexOf('?');
  const search = searchIndex >= 0 ? withoutHash.slice(searchIndex) : '';
  const pathname = searchIndex >= 0 ? withoutHash.slice(0, searchIndex) : withoutHash;
  return { pathname, search, hash };
};

/**
 * The given location's URL with the login dialog opened.
 *
 * Every entry point to the dialog goes through here, because "open the login dialog" has to mean
 * the same URL everywhere: the Topbar's account button, the guest menu, the contact-host button
 * on a listing, and the booking CTA in the order panel.
 *
 * The hash is carried through even though the dialog has no use for it, because dropping it would
 * make the change look like a navigation to isUiOnlyLocationChange — opening the dialog from a
 * URL with an anchor would then quietly refetch the page and scroll away from the anchor.
 *
 * @param {Object} location - current location
 * @returns {string} path with `?authmodal=open`
 */
export const pathWithLoginModal = location => {
  const params = searchParamsOf(location.search);
  // The mobile menu has to give way, or the two overlays stack on top of each other.
  //
  // The order panel deliberately does NOT: below 768px it is a full-screen modal holding the dates
  // and seats the visitor just picked, and closing it destroys them. ModalInMobile flips Modal
  // between its portal and non-portal branches, which are different element types at the same
  // position, so React unmounts the subtree and the FinalForm remounts empty. The login dialog is
  // a portal and renders above it either way, so leaving the panel open costs nothing visually and
  // means dismissing the dialog returns the visitor to their selection instead of a blank form.
  params.delete(MOBILE_MENU_PARAM);
  params.set(AUTH_MODAL_PARAM, 'open');
  return `${location.pathname}${searchToString(params)}${location.hash || ''}`;
};

/**
 * The given location's URL with the login dialog closed.
 *
 * Also what the social-login buttons hand to the OAuth round-trip as `from`, so the identity
 * provider returns the visitor to the page they were reading. Closing and returning must agree on
 * the destination, so both read it from here.
 *
 * @param {Object} location - current location
 * @returns {string} path without `?authmodal`
 */
export const pathWithoutLoginModal = location => {
  const params = searchParamsOf(location.search);
  params.delete(AUTH_MODAL_PARAM);
  return `${location.pathname}${searchToString(params)}${location.hash || ''}`;
};

// Create slug from random texts
// From Gist thread: https://gist.github.com/mathewbyrne/1280286
export const createSlug = str => {
  let text = str
    .toString()
    .toLowerCase()
    .trim();

  const sets = [
    { to: 'a', from: 'ÀÁÂÃÄÅÆĀĂĄẠẢẤẦẨẪẬẮẰẲẴẶ' },
    { to: 'c', from: 'ÇĆĈČ' },
    { to: 'd', from: 'ÐĎĐÞ' },
    { to: 'e', from: 'ÈÉÊËĒĔĖĘĚẸẺẼẾỀỂỄỆ' },
    { to: 'g', from: 'ĜĞĢǴ' },
    { to: 'h', from: 'ĤḦ' },
    { to: 'i', from: 'ÌÍÎÏĨĪĮİỈỊ' },
    { to: 'j', from: 'Ĵ' },
    { to: 'ij', from: 'Ĳ' },
    { to: 'k', from: 'Ķ' },
    { to: 'l', from: 'ĹĻĽŁ' },
    { to: 'm', from: 'Ḿ' },
    { to: 'n', from: 'ÑŃŅŇ' },
    { to: 'o', from: 'ÒÓÔÕÖØŌŎŐỌỎỐỒỔỖỘỚỜỞỠỢǪǬƠ' },
    { to: 'oe', from: 'Œ' },
    { to: 'p', from: 'ṕ' },
    { to: 'r', from: 'ŔŖŘ' },
    { to: 's', from: 'ßŚŜŞŠ' },
    { to: 't', from: 'ŢŤ' },
    { to: 'u', from: 'ÙÚÛÜŨŪŬŮŰŲỤỦỨỪỬỮỰƯ' },
    { to: 'w', from: 'ẂŴẀẄ' },
    { to: 'x', from: 'ẍ' },
    { to: 'y', from: 'ÝŶŸỲỴỶỸ' },
    { to: 'z', from: 'ŹŻŽ' },
    { to: '-', from: "·/_,:;'" },
  ];

  sets.forEach(set => {
    text = text.replace(new RegExp(`[${set.from}]`, 'gi'), set.to);
  });

  const slug = encodeURIComponent(
    text
      .replace(/\s+/g, '-') // Replace spaces with -
      .replace(/[^\w-]+/g, '') // Remove all non-word chars
      .replace(/--+/g, '-') // Replace multiple - with single -
      .replace(/^-+/, '') // Trim - from start of text
      .replace(/-+$/, '') // Trim - from end of text
  );

  return slug.length > 0 ? slug : 'no-slug';
};

/**
 * Parse float from a string
 *
 * @param {String} str - string to parse
 *
 * @return {Number|null} number parsed from the string, null if not a number
 */
export const parseFloatNum = str => {
  const trimmed = str && typeof str.trim === 'function' ? str.trim() : null;
  if (!trimmed) {
    return null;
  }
  // Don't allow: '9asdf' -> Number.parseFloat('9asdf') === 9
  const isFloatShaped = /^-?\d+\.?\d*$/.test(trimmed);
  const num = parseFloat(trimmed);
  const isNumber = !isNaN(num);

  if (isFloatShaped && isNumber) {
    const [integerPart] = trimmed.split('.');
    const wholeNumber = parseInt(integerPart, 10);
    // Edge cases: Number.parseInt('-0').toString() === '0' and Number.parseInt('0009') === 9
    const isFullyParsedNum = wholeNumber === -0 || wholeNumber.toString() === integerPart;
    if (isFullyParsedNum) {
      return num;
    }
  }
  return null;
};

/**
 * Encode a location to use in a URL
 *
 * @param {LatLng} location - location instance to encode
 *
 * @return {String} location coordinates separated by a comma
 */
export const encodeLatLng = location => `${location.lat},${location.lng}`;

/**
 * Decode a location from a string
 *
 * @param {String} str - string encoded with `encodeLatLng`
 *
 * @return {LatLng|null} location instance, null if could not parse
 */
export const decodeLatLng = str => {
  const parts = str.split(',');
  if (parts.length !== 2) {
    return null;
  }
  const lat = parseFloatNum(parts[0]);
  const lng = parseFloatNum(parts[1]);
  if (lat === null || lng === null) {
    return null;
  }
  return new LatLng(lat, lng);
};

/**
 * Encode a location bounds to use in a URL
 *
 * @param {LatLngBounds} bounds - bounds instance to encode
 *
 * @return {String} bounds coordinates separated by a comma
 */
export const encodeLatLngBounds = bounds => `${encodeLatLng(bounds.ne)},${encodeLatLng(bounds.sw)}`;

/**
 * Decode a location bounds from a string
 *
 * @param {String} str - string encoded with `encodeLatLngBounds`
 *
 * @return {LatLngBounds|null} location bounds instance, null if could not parse
 */
export const decodeLatLngBounds = str => {
  const parts = str.split(',');
  if (parts.length !== 4) {
    return null;
  }
  const ne = decodeLatLng(`${parts[0]},${parts[1]}`);
  const sw = decodeLatLng(`${parts[2]},${parts[3]}`);
  if (ne === null || sw === null) {
    return null;
  }
  return new LatLngBounds(ne, sw);
};

// Serialise SDK types in given object values into strings
const serialiseSdkTypes = obj =>
  Object.keys(obj).reduce((result, key) => {
    const val = obj[key];
    if (val instanceof LatLngBounds) {
      result[key] = encodeLatLngBounds(val);
    } else if (val instanceof LatLng) {
      result[key] = encodeLatLng(val);
    } else {
      result[key] = val;
    }
    return result;
  }, {});

/**
 * Serialise given object into a string that can be used in a
 * URL. Encode SDK types into a format that can be parsed with `parse`
 * defined below.
 *
 * @param {Object} params - object with strings/numbers/booleans or
 * SDK types as values
 *
 * @return {String} query string with sorted keys and serialised
 * values, `undefined` and `null` values are removed
 */
export const stringify = params => {
  const serialised = serialiseSdkTypes(params);
  const sorted = Object.keys(serialised).sort();

  const cleaned = sorted.reduce((result, key) => {
    const val = serialised[key];
    if (val !== null && val !== undefined) {
      result[key] = val;
    }
    return result;
  }, {});
  // Note: We previously used query-string library to stringify. It encoded spaces as '%20',
  // but URLSearchParams encodes spaces as '+'. If this matters, we could replace + with %20.
  // return new URLSearchParams(cleaned).toString().replace(/\+/g, '%20')
  return new URLSearchParams(cleaned).toString();
};

/**
 * Parse a URL search query. Converts numeric values into numbers,
 * 'true' and 'false' as booleans, and serialised LatLng and
 * LatLngBounds into respective instances based on given options.
 *
 * @param {String} search - query string to parse, optionally with a
 * leading '?' or '#' character
 *
 * @param {Object} options - Options for parsing:
 *
 * - latlng {Array<String} keys to parse as LatLng instances, null if
 *   not able to parse
 * - latlngBounds {Array<String} keys to parse as LatLngBounds
 *   instances, null if not able to parse
 *
 * @return {Object} key/value pairs parsed from the given String
 */
export const parse = (search, options = {}) => {
  const { latlng = [], latlngBounds = [] } = options;
  const searchString = typeof search === 'string' ? search : '';
  const params = new URLSearchParams(searchString);
  return Array.from(params.entries()).reduce((result, [key, val]) => {
    if (latlng.includes(key)) {
      result[key] = decodeLatLng(val);
    } else if (latlngBounds.includes(key)) {
      result[key] = decodeLatLngBounds(val);
    } else if (val === 'true') {
      result[key] = true;
    } else if (val === 'false') {
      result[key] = false;
    } else {
      const num = parseFloatNum(val);
      result[key] = num === null ? val : num;
    }
    return result;
  }, {});
};

/**
 * Create Twitter page url from twitterHandle
 *
 * @param {String} twitterHandle - handle is used for generating Twitter page URL
 *
 * @return {String} twitterPageURL
 */
export const twitterPageURL = twitterHandle => {
  if (twitterHandle && twitterHandle.charAt(0) === '@') {
    return `https://twitter.com/${twitterHandle.substring(1)}`;
  } else if (twitterHandle) {
    return `https://twitter.com/${twitterHandle}`;
  }
  return null;
};

/**
 * Check that the provided sort matches one of the accepted options
 *
 * @param {String} sort - Sort parameter
 *
 * @return {Object} Returns sort parameter if valid, otherwise empty object
 */
export const getValidInboxSort = sort => {
  const validOptions = ['createdAt', 'lastMessageAt', 'lastTransitionedAt'];
  // Discard invalid sorting options
  if (!validOptions.includes(sort)) {
    return {};
  }
  // Enforce createdAt order for those returned transactions that don't have messages.
  // Background: API does not guarantee the order of responses if the primary sort property is missing.
  return sort === 'lastMessageAt' ? { sort: 'lastMessageAt,createdAt' } : { sort };
};

/**
 * Hosts that may legitimately appear in a listing's `publicData.externalBookingUrl`.
 *
 * Today the only external booking flow is the LoungePair affiliate deeplink used by the
 * imported airport lounges. Add a host here when a new partner is onboarded — subdomains
 * of a listed host are accepted, everything else is not.
 */
export const EXTERNAL_BOOKING_HOSTS = ['loungepair.com'];

/**
 * Validates a provider-supplied external booking URL before it is rendered.
 *
 * `publicData` is provider-controlled: anyone who can edit a listing can put an arbitrary
 * value here, and the OrderPanel renders it as the primary "book now" CTA underneath the
 * marketplace's own trust badges, while CustomListingFields hides the field so the visitor
 * never sees where the button leads. An unchecked value therefore lets a provider borrow
 * our branding to send visitors to a page of their choosing.
 *
 * Callers should treat `null` as "there is no external booking URL" and fall back to the
 * normal in-marketplace booking panel.
 *
 * @param {unknown} value - raw `publicData.externalBookingUrl`
 * @returns {string|null} the normalized URL, or null when it is missing or not trustworthy
 */
export const safeExternalBookingUrl = value => {
  if (typeof value !== 'string') {
    return null;
  }

  let url;
  try {
    // Throws for relative and protocol-relative values ('//evil.example'), so those are
    // rejected here rather than resolving against the marketplace origin at render time.
    url = new URL(value);
  } catch (e) {
    return null;
  }

  // Only https: a partner booking page handling payments has no reason to be anything else,
  // and this also rules out javascript:, data: and other scheme tricks.
  if (url.protocol !== 'https:') {
    return null;
  }

  // 'https://loungepair.com@evil.example/' has hostname evil.example, so the allowlist below
  // already rejects it. Userinfo is never legitimate here, so refuse it outright.
  if (url.username || url.password) {
    return null;
  }

  const hostname = url.hostname.toLowerCase();
  const isAllowedHost = EXTERNAL_BOOKING_HOSTS.some(
    host => hostname === host || hostname.endsWith(`.${host}`)
  );

  return isAllowedHost ? url.href : null;
};

// 8-4-4-4-12 hex digits — the shape of every Sharetribe resource id. Deliberately loose about the
// version and variant nibbles: the API is the authority on whether an id exists, this only tells
// apart an id from a string that can never be one.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Whether a path parameter has the shape of a resource id (UUID).
 *
 * `/l/foo` used to answer HTTP 200 with a "listing is loading" shell — a soft 404 that Google
 * counts against the site. A value that can never be an id is a plain 404, and there is no point
 * in asking the API about it first.
 *
 * @param {unknown} value - e.g. `params.id` of a listing route
 * @returns {boolean}
 */
export const isUUID = value => typeof value === 'string' && UUID_PATTERN.test(value);
